import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Mail, Smartphone, MessageCircle, Camera, MessageSquare, FileText, BrainCircuit, Clock3, Send, Paperclip, X } from "lucide-react";
import { useApi, useApiMutation } from "@/hooks/use-api";
import { useCannedResponses, recordUsage } from "@/hooks/use-canned-responses";
import { useEmailTemplates } from "@/hooks/use-email-templates";
import { apiFetch } from "@/lib/api-client";
import type { CannedResponse } from "@/hooks/use-canned-responses";
import type { ChangeEvent, ReactNode } from "react";
import { ConversationAssist } from "./conversation-assist";
import { computeEditDistance, sendAiFeedbackSignal } from "@/lib/ai-feedback";
import { formatCurrency, formatMonthDayTime, formatTime } from "@/lib/i18n";

interface Message {
  id: string;
  direction: "inbound" | "outbound";
  channel: string;
  body: string;
  contentType?: string;
  senderType: "contact" | "agent" | "system";
  senderName: string;
  createdAt: string;
  isInternalNote?: boolean;
  attachments?: {
    count: number;
    types: string[];
    mediaId: string | null;
    mimeType: string | null;
  } | null;
  delivery?: {
    status: string;
    deliveredAt: string | null;
    readAt: string | null;
    provider: string | null;
  } | null;
}

interface TelegramInlineButton {
  text: string;
  callbackData?: string;
  url?: string;
}

interface ConversationThreadProps {
  conversationId: string;
  onToggleContactPanel: () => void;
  refreshToken?: number;
}

interface ConversationSummaryResponse {
  summary: string;
  topics: string[];
  commitments: string[];
  sentiment: "positive" | "neutral" | "negative";
  aiGenerated: boolean;
}

interface ConversationDetail {
  channel: string;
  status?: string;
  snoozeUntil?: string | null;
  snoozeType?: string | null;
  whatsappSession?: {
    isOpen: boolean;
    lastInboundAt: string | null;
    windowExpiresAt: string | null;
  } | null;
  whatsappBilling?: {
    category: string;
    openedAt: string | null;
    closedAt: string | null;
    messageCount: number;
    billedAmountCents: number;
    country: string | null;
    isOpen: boolean;
  } | null;
  aiSummary?: {
    summary: string;
    generatedAt: string;
    aiGenerated: boolean;
    messageCount: number;
    topics: string[];
    commitments: string[];
    sentiment: "positive" | "neutral" | "negative";
  } | null;
  messages: Message[];
}

interface ConversationContact {
  id: string;
  name: string;
  email: string;
  phone: string;
}

const CHANNELS = [
  { id: "email", label: "Email", icon: <Mail className="w-3.5 h-3.5" />, color: "bg-[var(--color-surface)] border-[var(--color-border)]" },
  { id: "sms", label: "SMS", icon: <Smartphone className="w-3.5 h-3.5" />, color: "bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20" },
  { id: "whatsapp", label: "WhatsApp", icon: <MessageCircle className="w-3.5 h-3.5" />, color: "bg-[var(--color-success)]/5 border-[var(--color-success)]/20" },
  { id: "telegram", label: "Telegram", icon: <Send className="w-3.5 h-3.5" />, color: "bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20" },
  { id: "instagram_dm", label: "Instagram", icon: <Camera className="w-3.5 h-3.5" />, color: "bg-[var(--color-ai)]/5 border-[var(--color-ai)]/20" },
  { id: "facebook_messenger", label: "Messenger", icon: <MessageSquare className="w-3.5 h-3.5" />, color: "bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20" },
] as const satisfies readonly { id: string; label: string; icon: ReactNode; color: string }[];

const CHANNEL_STYLES: Record<string, { bg: string; border: string }> = {
  whatsapp: { bg: "bg-[var(--color-success)]/5", border: "border-[var(--color-success)]/20" },
  sms: { bg: "bg-[var(--color-primary)]/5", border: "border-[var(--color-primary)]/20" },
  telegram: { bg: "bg-[var(--color-primary)]/5", border: "border-[var(--color-primary)]/20" },
  email: { bg: "bg-[var(--color-surface)]", border: "border-[var(--color-border)]" },
  instagram_dm: { bg: "bg-[var(--color-ai)]/5", border: "border-[var(--color-ai)]/20" },
  facebook_messenger: { bg: "bg-[var(--color-primary)]/5", border: "border-[var(--color-primary)]/20" },
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  image: "Image",
  video: "Video",
  document: "Document",
  audio: "Audio",
  template: "Template",
  location: "Location",
  attachment: "Attachment",
  internal_note: "Internal note",
};

function normalizeMediaLabel(value: string): string {
  return CONTENT_TYPE_LABELS[value] ?? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatAttachmentSummary(message: Message): string {
  if (message.attachments?.types?.length) {
    return message.attachments.types.map(normalizeMediaLabel).join(", ");
  }

  if (message.contentType && message.contentType !== "text") {
    return normalizeMediaLabel(message.contentType);
  }

  return "Attachment";
}

function formatDeliveryStatus(message: Message): string | null {
  if (!message.delivery?.status || message.direction !== "outbound") {
    return null;
  }

  if (message.delivery.readAt) {
    return `Read ${formatTime(message.delivery.readAt)}`;
  }

  if (message.delivery.deliveredAt) {
    return `Delivered ${formatTime(message.delivery.deliveredAt)}`;
  }

  return message.delivery.status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

const MAX_EMAIL_ATTACHMENTS = 5;
const MAX_EMAIL_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const TELEGRAM_QUICK_ACTIONS: readonly {
  id: string;
  label: string;
  body: string;
  inlineKeyboard: TelegramInlineButton[][];
}[] = [
  {
    id: "book_call",
    label: "Book call",
    body: "I can help. Choose an option below and I’ll route it from here.",
    inlineKeyboard: [[
      { text: "Book a call", callbackData: "book_call" },
      { text: "Ask a question", callbackData: "ask_question" },
    ]],
  },
  {
    id: "show_options",
    label: "Show options",
    body: "Here are the fastest options. Tap one and I’ll continue from there.",
    inlineKeyboard: [[
      { text: "Pricing", callbackData: "pricing" },
      { text: "Support", callbackData: "support" },
    ]],
  },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Sprint 3 A1-A7: Full conversation thread with:
 * - Channel selector for cross-channel reply (A1)
 * - Internal note toggle (A2)
 * - Quick reply /command support (A7)
 * - Keyboard shortcuts: R=reply, N=note, E=close
 */
export function ConversationThread({ conversationId, onToggleContactPanel, refreshToken = 0 }: ConversationThreadProps) {
  const [replyText, setReplyText] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("email");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [interactionError, setInteractionError] = useState<string | null>(null);
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [quickReplyFilter, setQuickReplyFilter] = useState("");
  const [quickReplyIndex, setQuickReplyIndex] = useState(0);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [customSnoozeAt, setCustomSnoozeAt] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [telegramInlineKeyboard, setTelegramInlineKeyboard] = useState<TelegramInlineButton[][] | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<{
    text: string;
    suggestionIndex: number;
    aiGenerated: boolean;
  } | null>(null);
  const hasInitializedReplyChannelRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Fetch canned responses for quick reply
  const { responses: cannedResponses } = useCannedResponses();
  const { templates: emailTemplates } = useEmailTemplates();

  const filteredCannedResponses = useMemo(() => {
    if (!showQuickReply) return [];
    const query = quickReplyFilter.toLowerCase();
    return cannedResponses.filter((r) => {
      const shortcut = r.shortcut?.toLowerCase() ?? "";
      const title = r.title.toLowerCase();
      return shortcut.startsWith(query) || title.includes(query);
    }).slice(0, 8);
  }, [showQuickReply, quickReplyFilter, cannedResponses]);
  const availableEmailTemplates = useMemo(
    () => emailTemplates.filter((template) => {
      const body = template.textContent?.trim() || htmlToPlainText(template.htmlContent);
      return body.length > 0;
    }),
    [emailTemplates],
  );

  // GET /v1/inbox/conversations/:id returns { data: { ...conv, messages } }
  const { data: conversationDetail, refetch: refetchConversation } = useApi<ConversationDetail>(
    conversationId ? `/v1/inbox/conversations/${conversationId}` : "",
    { enabled: !!conversationId },
  );
  const { data: contactDetail } = useApi<ConversationContact>(
    conversationId ? `/v1/inbox/conversations/${conversationId}/contact` : "",
    { enabled: !!conversationId },
  );
  const messages: Message[] = conversationDetail?.messages ?? [];
  const conversationChannel = conversationDetail?.channel ?? "email";
  const lastInboundChannel = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.direction === "inbound") {
        return message.channel;
      }
    }

    return conversationChannel;
  }, [conversationChannel, messages]);
  const conversationSummary = conversationDetail?.aiSummary ?? null;
  const whatsappSession = conversationDetail?.whatsappSession ?? null;
  const whatsappBilling = conversationDetail?.whatsappBilling ?? null;
  const isSnoozed = conversationDetail?.status === "snoozed";
  const hasContactEmail = Boolean(contactDetail?.email && contactDetail.email !== "—");
  const hasContactPhone = Boolean(contactDetail?.phone && contactDetail.phone !== "—");
  const availableChannels = useMemo(() => {
    return CHANNELS.filter((channel) => {
      if (channel.id === conversationChannel) {
        return true;
      }
      if (channel.id === "email") {
        return hasContactEmail;
      }
      if (channel.id === "sms" || channel.id === "whatsapp") {
        return hasContactPhone;
      }
      return false;
    });
  }, [conversationChannel, hasContactEmail, hasContactPhone]);

  const { mutate: generateSummary, isLoading: isGeneratingSummary } = useApiMutation<undefined, ConversationSummaryResponse>(
    `/v1/inbox/conversations/${conversationId}/summarize`,
  );
  const { mutate: updateStatus } = useApiMutation<
    { status: "open" | "closed" | "snoozed"; snoozeUntil?: string },
    { status: string }
  >(`/v1/inbox/conversations/${conversationId}/status`);
  const { mutate: snoozeConversation } = useApiMutation<
    { mode: "one_hour" | "tomorrow_morning" | "next_week" | "when_reply" | "custom"; customUntil?: string },
    { status: string; snoozeUntil: string | null; snoozeType: string }
  >(`/v1/inbox/conversations/${conversationId}/snooze`);

  useEffect(() => {
    if (refreshToken <= 0) {
      return;
    }

    refetchConversation();
  }, [refreshToken, refetchConversation]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    hasInitializedReplyChannelRef.current = false;
  }, [conversationId]);

  useEffect(() => {
    if (!hasInitializedReplyChannelRef.current) {
      hasInitializedReplyChannelRef.current = true;

      if (availableChannels.some((channel) => channel.id === lastInboundChannel)) {
        setSelectedChannel(lastInboundChannel);
        return;
      }

      if (availableChannels[0]) {
        setSelectedChannel(availableChannels[0].id);
      }
      return;
    }

    if (!availableChannels.some((channel) => channel.id === selectedChannel) && availableChannels[0]) {
      setSelectedChannel(availableChannels[0].id);
    }
  }, [availableChannels, lastInboundChannel, selectedChannel]);

  useEffect(() => {
    if (selectedChannel !== "email" || isInternalNote) {
      setShowEmailTemplates(false);
      setSelectedAttachments([]);
    }
    if (selectedChannel !== "telegram" || isInternalNote) {
      setTelegramInlineKeyboard(null);
    }
  }, [isInternalNote, selectedChannel]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      if (e.key === "r" || e.key === "R") { textareaRef.current?.focus(); e.preventDefault(); }
      if (e.key === "n" || e.key === "N") { setIsInternalNote(true); textareaRef.current?.focus(); e.preventDefault(); }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // A7: Quick reply detection — show dropdown when input starts with "/"
  const handleTextChange = useCallback((text: string) => {
    setReplyText(text);
    if (selectedSuggestion && text.trim() !== selectedSuggestion.text.trim()) {
      setSelectedSuggestion((current) => current ? { ...current, text: current.text } : current);
    }
    if (text.startsWith("/")) {
      setShowQuickReply(true);
      setQuickReplyFilter(text.slice(1));
      setQuickReplyIndex(0);
    } else {
      setShowQuickReply(false);
      setQuickReplyFilter("");
    }
  }, [selectedSuggestion]);

  const selectQuickReply = useCallback((response: CannedResponse) => {
    setReplyText(response.body);
    setShowQuickReply(false);
    setQuickReplyFilter("");
    setInteractionError(null);
    textareaRef.current?.focus();
    void recordUsage(response.id).catch(() => {
      setInteractionError("Could not record canned response usage.");
    });
  }, []);

  const selectEmailTemplate = useCallback((templateId: string) => {
    const template = availableEmailTemplates.find((item) => item.id === templateId);
    if (!template) {
      return;
    }

    const body = template.textContent?.trim() || htmlToPlainText(template.htmlContent);
    if (!body) {
      return;
    }

    setReplyText(body);
    setShowEmailTemplates(false);
    setInteractionError(null);
    textareaRef.current?.focus();
  }, [availableEmailTemplates]);

  const handleSelectSuggestion = useCallback((text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => {
    setReplyText(text);
    setSelectedSuggestion({
      text,
      suggestionIndex: meta.suggestionIndex,
      aiGenerated: meta.aiGenerated,
    });
    setInteractionError(null);
    textareaRef.current?.focus();
  }, []);

  const handleRejectSuggestion = useCallback(async (text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => {
    try {
      await sendAiFeedbackSignal({
        signalType: "inbox.reply_suggestion.rejected",
        aiOutputId: `${conversationId}:suggestion:${meta.suggestionIndex}`,
        aiOutput: {
          text,
          aiGenerated: meta.aiGenerated,
        },
        humanAction: {
          action: "reject",
        },
        context: {
          touchpoint: "inbox.reply_suggestions",
          conversationId,
        },
      });
    } catch {
      setInteractionError("Could not record the rejected suggestion.");
    }
  }, [conversationId]);

  const handleGenerateSummary = useCallback(async () => {
    const response = await generateSummary(undefined);
    if (!response) {
      setInteractionError("Could not generate the conversation summary.");
      return;
    }
    setInteractionError(null);
    refetchConversation();
  }, [generateSummary, refetchConversation]);

  const handleSnooze = useCallback(async (
    variant: "one_hour" | "tomorrow_morning" | "next_week" | "when_reply" | "custom",
  ) => {
    const result = await snoozeConversation({
      mode: variant,
      customUntil: variant === "custom" ? new Date(customSnoozeAt).toISOString() : undefined,
    });
    if (!result) {
      setInteractionError("Could not snooze the conversation.");
      return;
    }
    setInteractionError(null);
    if (variant === "custom") {
      setCustomSnoozeAt("");
    }
    refetchConversation();
  }, [customSnoozeAt, refetchConversation, snoozeConversation]);

  const handleUnsnooze = useCallback(async () => {
    const result = await updateStatus({ status: "open" });
    if (!result) {
      setInteractionError("Could not return the conversation to the open queue.");
      return;
    }
    setInteractionError(null);
    refetchConversation();
  }, [refetchConversation, updateStatus]);

  // POST /v1/inbox/conversations/:id/messages — mounted on api-email Worker
  const { mutate: sendMessage, error: sendError } = useApiMutation<
    { body: string; channel: string; isInternal: boolean; telegramInlineKeyboard?: TelegramInlineButton[][] },
    { id: string }
  >(`/v1/inbox/conversations/${conversationId}/messages`);

  const handleAttachmentSelection = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const incomingFiles = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    if (incomingFiles.length === 0) {
      return;
    }

    const oversizedFile = incomingFiles.find((file) => file.size > MAX_EMAIL_ATTACHMENT_BYTES);
    if (oversizedFile) {
      setInteractionError(`${oversizedFile.name} is larger than 5MB.`);
      return;
    }

    setSelectedAttachments((current) => {
      const next = [...current, ...incomingFiles].slice(0, MAX_EMAIL_ATTACHMENTS);
      if (current.length + incomingFiles.length > MAX_EMAIL_ATTACHMENTS) {
        setInteractionError(`You can attach up to ${MAX_EMAIL_ATTACHMENTS} files per email reply.`);
      } else {
        setInteractionError(null);
      }
      return next;
    });
  }, []);

  const removeAttachment = useCallback((indexToRemove: number) => {
    setSelectedAttachments((current) => current.filter((_, index) => index !== indexToRemove));
  }, []);

  const selectTelegramQuickAction = useCallback((action: (typeof TELEGRAM_QUICK_ACTIONS)[number]) => {
    setReplyText(action.body);
    setTelegramInlineKeyboard(action.inlineKeyboard);
    setInteractionError(null);
    textareaRef.current?.focus();
  }, []);

  const handleSend = useCallback(async () => {
    if (!replyText.trim()) return;
    const trimmedReply = replyText.trim();
    const response = selectedAttachments.length > 0
      ? await (async () => {
        const formData = new FormData();
        formData.set("body", trimmedReply);
        formData.set("channel", selectedChannel);
        formData.set("isInternal", String(isInternalNote));
        selectedAttachments.forEach((file) => {
          formData.append("attachments", file, file.name);
        });

        try {
          return await apiFetch<{ data: { id: string } }>(`/v1/inbox/conversations/${conversationId}/messages`, {
            method: "POST",
            body: formData,
          });
        } catch (error) {
          setInteractionError(error instanceof Error ? error.message : "Could not send the reply with attachments.");
          return null;
        }
      })()
      : await sendMessage({
        body: trimmedReply,
        channel: selectedChannel,
        isInternal: isInternalNote,
        ...(selectedChannel === "telegram" && telegramInlineKeyboard ? { telegramInlineKeyboard } : {}),
      });
    if (!response) {
      setInteractionError(isInternalNote ? "Could not save the internal note." : "Could not send the reply.");
      return;
    }
    if (!isInternalNote && selectedSuggestion) {
      const editDistance = computeEditDistance(selectedSuggestion.text.trim(), trimmedReply);
      const isAcceptedVerbatim = editDistance === 0;

      void sendAiFeedbackSignal({
        signalType: isAcceptedVerbatim ? "inbox.reply_suggestion.accepted" : "inbox.reply_suggestion.edited",
        aiOutputId: `${conversationId}:suggestion:${selectedSuggestion.suggestionIndex}`,
        aiOutput: {
          text: selectedSuggestion.text,
          aiGenerated: selectedSuggestion.aiGenerated,
        },
        humanAction: {
          action: isAcceptedVerbatim ? "accept" : "edit",
          finalText: trimmedReply,
          editDistance,
        },
        context: {
          touchpoint: "inbox.reply_suggestions",
          conversationId,
          channel: selectedChannel,
        },
      }).catch(() => {
        setInteractionError("Reply sent, but the AI feedback signal could not be recorded.");
      });
    }
    setInteractionError(null);
    setReplyText("");
    setIsInternalNote(false);
    setShowQuickReply(false);
    setQuickReplyFilter("");
    setSelectedAttachments([]);
    setSelectedSuggestion(null);
    setTelegramInlineKeyboard(null);
  }, [conversationId, isInternalNote, replyText, selectedAttachments, selectedChannel, selectedSuggestion, sendMessage, telegramInlineKeyboard]);

  return (
    <div className="flex flex-col h-full">
      {/* Thread Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-background)]">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">
            Conversation #{conversationId.slice(0, 8)}
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span>{conversationChannel}</span>
            {isSnoozed ? (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-warning)]/10 px-2 py-0.5 text-[var(--color-warning)]">
                  <Clock3 className="h-3 w-3" />
                  Snoozed
                  {conversationDetail?.snoozeType === "when_reply" ? " until they reply" : ""}
                  {conversationDetail?.snoozeUntil ? ` until ${formatMonthDayTime(conversationDetail.snoozeUntil)}` : ""}
                </span>
              </>
            ) : null}
            {conversationChannel === "whatsapp" && whatsappSession ? (
              <>
                <span>·</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                    whatsappSession.isOpen
                      ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                      : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                  }`}
                >
                  <MessageCircle className="h-3 w-3" />
                  {whatsappSession.isOpen ? "24h reply window open" : "24h reply window expired"}
                  {whatsappSession.windowExpiresAt
                    ? ` · ${whatsappSession.isOpen ? "closes" : "closed"} ${formatMonthDayTime(whatsappSession.windowExpiresAt)}`
                    : ""}
                </span>
              </>
            ) : null}
            {conversationChannel === "whatsapp" && whatsappBilling ? (
              <>
                <span>·</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                    whatsappBilling.isOpen
                      ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                      : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                  }`}
                >
                  <FileText className="h-3 w-3" />
                  {whatsappBilling.category} billing
                  {` · ${formatCurrency(whatsappBilling.billedAmountCents, "USD", "en-US")}`}
                  {` · ${whatsappBilling.messageCount} msgs`}
                  {whatsappBilling.country ? ` · ${whatsappBilling.country}` : ""}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSnoozed ? (
            <button
              type="button"
              onClick={() => void handleUnsnooze()}
              className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
            >
              Resume
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleSnooze("one_hour")}
                className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
              >
                Snooze 1h
              </button>
              <button
                type="button"
                onClick={() => void handleSnooze("tomorrow_morning")}
                className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
              >
                Tomorrow 9am
              </button>
              <button
                type="button"
                onClick={() => void handleSnooze("next_week")}
                className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
              >
                Next week
              </button>
              <button
                type="button"
                onClick={() => void handleSnooze("when_reply")}
                className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
              >
                When they reply
              </button>
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={customSnoozeAt}
                  onChange={(event) => setCustomSnoozeAt(event.target.value)}
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)]"
                />
                <button
                  type="button"
                  onClick={() => void handleSnooze("custom")}
                  disabled={!customSnoozeAt}
                  className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Custom
                </button>
              </div>
            </>
          )}
          <button onClick={onToggleContactPanel} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] text-[var(--color-muted-foreground)]" title="Toggle contact panel (Ctrl+I)">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="border-b border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
              <BrainCircuit className="h-3.5 w-3.5" />
              Conversation summary
            </div>
            {conversationSummary ? (
              <>
                <p className="mt-2 text-sm text-[var(--color-foreground)]">{conversationSummary.summary}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Generated {formatMonthDayTime(conversationSummary.generatedAt)}
                  {" · "}
                  {conversationSummary.messageCount} messages analyzed
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-[var(--color-muted-foreground)]">
                    Sentiment: {conversationSummary.sentiment}
                  </span>
                  {conversationSummary.topics.map((topic) => (
                    <span
                      key={topic}
                      className="rounded-full border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-2 py-1 text-[var(--color-ai)]"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
                {conversationSummary.commitments.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                      Commitments
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-[var(--color-foreground)]">
                      {conversationSummary.commitments.map((commitment) => (
                        <li key={commitment}>• {commitment}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                No summary has been generated yet for this thread.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => void handleGenerateSummary()}
            disabled={isGeneratingSummary}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)] disabled:opacity-50"
          >
            {isGeneratingSummary ? "Generating…" : conversationSummary ? "Refresh summary" : "Generate summary"}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        <ConversationAssist
          conversationId={conversationId}
          conversationChannel={conversationChannel}
          messages={messages}
          onSelectSuggestion={handleSelectSuggestion}
          onRejectSuggestion={handleRejectSuggestion}
        />
        {messages.length === 0 ? (
          <div className="text-center text-[var(--color-muted-foreground)] text-sm py-12">
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => {
            const defaultStyle = { bg: "bg-[var(--color-surface)]", border: "border-[var(--color-border)]" };
            const channelStyle = CHANNEL_STYLES[msg.channel] ?? defaultStyle;
            const isOutbound = msg.direction === "outbound";
            const isNote = msg.isInternalNote;
            const deliveryStatus = formatDeliveryStatus(msg);

            return (
              <div key={msg.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[70%] rounded-lg px-3 py-2 ${
                  isNote
                    ? "bg-[var(--color-warning)]/5 border border-[var(--color-warning)]/20 dark:bg-yellow-900/20 dark:border-yellow-700"
                    : isOutbound
                      ? "bg-[var(--color-primary)] text-white"
                      : `${channelStyle.bg} border ${channelStyle.border}`
                }`}>
                  {isNote && (
                    <span className="text-xs font-medium text-[var(--color-warning)] dark:text-yellow-400 block mb-1">Internal Note</span>
                  )}
                  {msg.contentType && msg.contentType !== "text" && !isNote && (
                    <span className={`mb-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${isOutbound ? "border-white/20 bg-white/10 text-white" : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-muted-foreground)]"}`}>
                      {CONTENT_TYPE_LABELS[msg.contentType] ?? msg.contentType}
                    </span>
                  )}
                  {msg.attachments && (
                    <div className={`mb-2 rounded-md border px-2.5 py-2 text-xs ${isOutbound && !isNote ? "border-white/15 bg-white/10 text-white/90" : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-muted-foreground)]"}`}>
                      <div className="font-medium">
                        {formatAttachmentSummary(msg)}
                      </div>
                      <div className="mt-1">
                        {msg.attachments.count} attachment{msg.attachments.count === 1 ? "" : "s"}
                      </div>
                      {msg.attachments.types.length > 0 && (
                        <div className="mt-1">
                          Types: {msg.attachments.types.map(normalizeMediaLabel).join(", ")}
                        </div>
                      )}
                      {msg.attachments.mimeType && (
                        <div className="mt-1">
                          MIME: {msg.attachments.mimeType}
                        </div>
                      )}
                      {msg.attachments.mediaId && (
                        <div className="mt-1 break-all">
                          Media reference: {msg.attachments.mediaId}
                        </div>
                      )}
                    </div>
                  )}
                  <p className={`text-sm whitespace-pre-wrap ${isOutbound && !isNote ? "text-white" : "text-[var(--color-foreground)]"}`}>
                    {/* React auto-escapes. If switching to HTML rendering, use DOMPurify */}
                    {msg.body}
                  </p>
                  <div className={`flex items-center gap-2 mt-1 text-xs ${isOutbound && !isNote ? "text-[var(--color-primary)]/30" : "text-[var(--color-muted-foreground)]"}`}>
                    <span>{msg.senderName}</span>
                    <span>·</span>
                    <span>{formatTime(msg.createdAt)}</span>
                    {deliveryStatus ? (
                      <>
                        <span>·</span>
                        <span>{deliveryStatus}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Reply Suggestions (A7) — powered by canned responses API */}
      {showQuickReply && filteredCannedResponses.length > 0 && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2">
          <div className="text-xs text-[var(--color-muted-foreground)] mb-1 px-3">
            Canned responses — arrow keys to navigate, Enter to select, Escape to close
          </div>
          {filteredCannedResponses.map((r, idx) => (
            <button
              key={r.id}
              onClick={() => selectQuickReply(r)}
              className={`w-full text-left px-3 py-1.5 text-sm rounded ${
                idx === quickReplyIndex ? "bg-[var(--color-muted)]" : "hover:bg-[var(--color-muted)]"
              }`}
            >
              {r.shortcut && (
                <span className="font-mono text-[var(--color-primary)] dark:text-[var(--color-primary)]/70">/{r.shortcut}</span>
              )}
              <span className="font-medium text-[var(--color-foreground)] ml-2">{r.title}</span>
              <span className="text-[var(--color-muted-foreground)] ml-2 truncate">{r.body.slice(0, 50)}</span>
            </button>
          ))}
        </div>
      )}

      {showEmailTemplates && selectedChannel === "email" && !isInternalNote && availableEmailTemplates.length > 0 && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2">
          <div className="mb-1 px-3 text-xs text-[var(--color-muted-foreground)]">
            Email templates from the mounted library
          </div>
          {availableEmailTemplates.slice(0, 8).map((template) => {
            const preview = template.textContent?.trim() || htmlToPlainText(template.htmlContent);
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => selectEmailTemplate(template.id)}
                className="w-full rounded px-3 py-2 text-left text-sm hover:bg-[var(--color-muted)]"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--color-foreground)]">{template.name}</span>
                  {template.subject && (
                    <span className="text-xs text-[var(--color-muted-foreground)]">{template.subject}</span>
                  )}
                </div>
                <div className="mt-1 truncate text-[var(--color-muted-foreground)]">{preview.slice(0, 80)}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Reply Input (A1 + A2) */}
      <div className={`border-t px-4 py-3 ${isInternalNote ? "bg-[var(--color-warning)]/5 dark:bg-yellow-900/10 border-[var(--color-warning)]/20" : "bg-[var(--color-background)] border-[var(--color-border)]"}`}>
        {(interactionError || sendError) && (
          <div className="mb-2 rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-3 py-2 text-sm text-[var(--color-warning)]">
            {interactionError ?? sendError}
          </div>
        )}
        {/* Channel selector + note toggle */}
        <div className="flex items-center gap-2 mb-2">
          {/* A1: Channel selector */}
          <div className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-1">
            {availableChannels.map((channel) => {
              const isActive = selectedChannel === channel.id;
              return (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() => setSelectedChannel(channel.id)}
                  disabled={isInternalNote}
                  aria-pressed={isActive}
                  title={`Reply via ${channel.label}`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                    isActive
                      ? "bg-[var(--color-primary)] text-white shadow-sm"
                      : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {channel.icon}
                  <span>{channel.label}</span>
                </button>
              );
            })}
          </div>

          {/* A2: Internal note toggle */}
          <button
            onClick={() => setIsInternalNote(!isInternalNote)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
              isInternalNote
                ? "bg-[var(--color-warning)]/10 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-600 dark:text-yellow-300"
                : "border-[var(--color-border)] hover:bg-[var(--color-muted)]"
            }`}
          >
            <FileText className="w-3.5 h-3.5" /> {isInternalNote ? "Note mode" : "Note"}
          </button>

          {selectedChannel === "email" && !isInternalNote && availableEmailTemplates.length > 0 && (
            <button
              type="button"
              onClick={() => setShowEmailTemplates((current) => !current)}
              className={`flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                showEmailTemplates
                  ? "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "border-[var(--color-border)] hover:bg-[var(--color-muted)]"
              }`}
            >
              <Mail className="h-3.5 w-3.5" /> Templates
            </button>
          )}

          <span className="text-xs text-[var(--color-muted-foreground)] ml-auto">R=reply, N=note, /=quick reply</span>
        </div>

        {selectedChannel === "telegram" && !isInternalNote ? (
          <div className="mb-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
            <div className="mb-2 text-xs font-medium text-[var(--color-foreground)]">Telegram inline actions</div>
            <div className="flex flex-wrap items-center gap-2">
              {TELEGRAM_QUICK_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => selectTelegramQuickAction(action)}
                  className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
                >
                  {action.label}
                </button>
              ))}
              {telegramInlineKeyboard ? (
                <span className="inline-flex items-center rounded-md bg-[var(--color-success)]/10 px-2.5 py-1.5 text-xs text-[var(--color-success)]">
                  {telegramInlineKeyboard.flat().length} inline action{telegramInlineKeyboard.flat().length === 1 ? "" : "s"} attached
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {selectedChannel === "email" && !isInternalNote && (
          <div className="mb-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                aria-label="Email attachments"
                className="hidden"
                onChange={handleAttachmentSelection}
              />
              <button
                type="button"
                onClick={() => attachmentInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach files
              </button>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                PDFs, images, docs. Max {MAX_EMAIL_ATTACHMENTS} files, 5MB each.
              </span>
            </div>

            {selectedAttachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedAttachments.map((file, index) => (
                  <span
                    key={`${file.name}-${file.size}-${index}`}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-1 text-xs text-[var(--color-foreground)]"
                  >
                    <Paperclip className="h-3 w-3 shrink-0 text-[var(--color-muted-foreground)]" />
                    <span className="max-w-[14rem] truncate">{file.name}</span>
                    <span className="text-[var(--color-muted-foreground)]">{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      aria-label={`Remove ${file.name}`}
                      className="rounded-full p-0.5 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Text input + send */}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={replyText}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (showQuickReply && filteredCannedResponses.length > 0) {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setQuickReplyIndex((prev) => Math.min(prev + 1, filteredCannedResponses.length - 1));
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setQuickReplyIndex((prev) => Math.max(prev - 1, 0));
                  return;
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  const selected = filteredCannedResponses[quickReplyIndex];
                  if (selected) selectQuickReply(selected);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setShowQuickReply(false);
                  setQuickReplyFilter("");
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
            placeholder={isInternalNote ? "Write an internal note..." : `Reply via ${CHANNELS.find(c => c.id === selectedChannel)?.label ?? "Email"}...`}
            rows={2}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm resize-none focus:ring-1 ${
              isInternalNote
                ? "border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500 bg-[var(--color-warning)]/5 dark:bg-yellow-900/20 dark:border-yellow-600"
                : "border-[var(--color-border)] focus:border-[var(--color-primary)]/80 focus:ring-[var(--color-ring)]"
            }`}
          />
          <button
            onClick={handleSend}
            disabled={!replyText.trim()}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              isInternalNote ? "bg-yellow-600 hover:bg-yellow-700" : "bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
            }`}
          >
            {isInternalNote ? "Add Note" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
